import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import {
  CSM_COPY,
  CSM_STATE,
  CSM_TYPE,
  CSM_FORM_COPY_OPTIONS,
  CSM_FORM_STATE_OPTIONS,
  ONLINE_PROVIDER_TRANSFER_OPTIONS,
  mergeCustomerServiceManagementDetailsWithLanguages,
  stripCustomerServiceManagementDetails,
} from './customerCenterManagementConstants';

const EMPTY_FORM = {
  customerServiceTypeId: '',
  type: CSM_TYPE.OFFLINE,
  onlineProviderId: -1,
  onlineProviderTransferType: -1,
  body: '',
  sort: 1,
  state: CSM_STATE.ON,
  copyStatus: CSM_COPY.OFF,
  image: '',
  details: [],
};

export default function CustomerCenterManagementEditModal({ open, managementId, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [languages, setLanguages] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [providerOptions, setProviderOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(managementId);
  const isOnline = Number(form.type) === CSM_TYPE.ONLINE;

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const [langList, providers, types] = await Promise.all([
          adminApi.newsLanguages(),
          adminApi.customerServiceOnlineProviders(),
          adminApi.customerServiceTypeOptions({ lang: 'en_US', state: 1 }),
        ]);
        if (cancelled) {
          return;
        }
        setLanguages(langList || []);
        setProviderOptions(providers || []);
        setTypeOptions(types || []);

        if (managementId) {
          const data = await adminApi.getCustomerServiceManagement(managementId);
          if (cancelled) {
            return;
          }
          setForm({
            customerServiceTypeId: data.customerServiceTypeId ?? '',
            type: data.type ?? CSM_TYPE.OFFLINE,
            onlineProviderId: data.onlineProviderId ?? -1,
            onlineProviderTransferType: data.onlineProviderTransferType ?? -1,
            body: data.body || '',
            sort: data.sort ?? 1,
            state: data.state ?? CSM_STATE.ON,
            copyStatus: data.copyStatus ?? CSM_COPY.OFF,
            image: data.image || '',
            details: mergeCustomerServiceManagementDetailsWithLanguages(langList, data.details),
          });
        } else {
          setForm({
            ...EMPTY_FORM,
            details: mergeCustomerServiceManagementDetailsWithLanguages(langList, []),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '載入失敗');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, managementId]);

  function handleTypeChange(typeId) {
    const selected = typeOptions.find((item) => String(item.value) === String(typeId));
    const nextType = selected?.type ?? CSM_TYPE.OFFLINE;
    setForm((prev) => ({
      ...prev,
      customerServiceTypeId: typeId,
      type: nextType,
      onlineProviderId: nextType === CSM_TYPE.ONLINE ? (prev.onlineProviderId > 0 ? prev.onlineProviderId : 1) : -1,
      onlineProviderTransferType:
        nextType === CSM_TYPE.ONLINE
          ? (prev.onlineProviderTransferType > 0 ? prev.onlineProviderTransferType : 1)
          : -1,
    }));
  }

  function updateDetail(index, key, value) {
    setForm((prev) => {
      const nextDetails = [...prev.details];
      nextDetails[index] = { ...nextDetails[index], [key]: value };
      return { ...prev, details: nextDetails };
    });
  }

  async function handleImageUpload(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setUploadingImage(true);
    setError('');
    try {
      const url = await adminApi.uploadOssImage(file);
      setForm((prev) => ({ ...prev, image: url }));
    } catch (err) {
      setError(err.message || '圖片上傳失敗');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    if (!form.customerServiceTypeId) {
      setError('請選擇所屬客服種類');
      return;
    }
    if (!form.image?.trim()) {
      setError('請上傳客服圖標');
      return;
    }
    const details = stripCustomerServiceManagementDetails(form.details).filter((item) => item.name?.trim());
    if (details.length === 0) {
      setError('至少填寫一個語系名稱');
      return;
    }
    const payload = {
      customerServiceTypeId: Number(form.customerServiceTypeId),
      type: Number(form.type),
      onlineProviderId: isOnline ? Number(form.onlineProviderId) : -1,
      onlineProviderTransferType: isOnline ? Number(form.onlineProviderTransferType) : -1,
      body: form.body || '',
      sort: Number(form.sort) || 1,
      state: Number(form.state),
      copyStatus: Number(form.copyStatus),
      image: form.image.trim(),
      details,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await adminApi.updateCustomerServiceManagement({ id: managementId, ...payload });
      } else {
        await adminApi.createCustomerServiceManagement(payload);
      }
      onSaved?.(isEdit);
      onClose?.();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="confirm-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__header">
          <h3>{isEdit ? '修改' : '新增'}</h3>
          <button type="button" className="btn btn-link" onClick={onClose}>
            ×
          </button>
        </div>

        <form className="modal-panel__body static-content-edit-form" onSubmit={handleSubmit}>
          {error ? <p className="form-message form-message--error">{error}</p> : null}
          {loading ? <p className="table-empty">載入中…</p> : null}

          {!loading ? (
            <>
              <div className="form-field">
                <label htmlFor="csmTypeId">所屬客服種類</label>
                <select
                  id="csmTypeId"
                  value={form.customerServiceTypeId}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  required
                >
                  <option value="">請選擇</option>
                  {typeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <span className="form-field__label">客服類別</span>
                <p className="form-static-text">
                  {isOnline ? '在線客服' : '線下客服'}
                </p>
              </div>

              {isOnline ? (
                <>
                  <div className="form-field">
                    <label htmlFor="csmProvider">在線客服服務商</label>
                    <select
                      id="csmProvider"
                      value={form.onlineProviderId}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, onlineProviderId: Number(e.target.value) }))
                      }
                      required
                    >
                      {providerOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label htmlFor="csmTransferType">顯示類型</label>
                    <select
                      id="csmTransferType"
                      value={form.onlineProviderTransferType}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          onlineProviderTransferType: Number(e.target.value),
                        }))
                      }
                      required
                    >
                      {ONLINE_PROVIDER_TRANSFER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : null}

              <div className="form-field">
                <label htmlFor="csmBody">顯示內容</label>
                <textarea
                  id="csmBody"
                  rows={3}
                  value={form.body}
                  onChange={(e) => setForm((prev) => ({ ...prev, body: e.target.value }))}
                  placeholder="請輸入第三方在線客服鏈接、用戶名、號碼或客服帳號"
                />
              </div>

              <div className="form-grid form-grid--2">
                <div className="form-field">
                  <label htmlFor="csmSort">顯示順序</label>
                  <input
                    id="csmSort"
                    type="number"
                    min="1"
                    value={form.sort}
                    onChange={(e) => setForm((prev) => ({ ...prev, sort: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <fieldset className="form-fieldset">
                <legend>狀態</legend>
                <div className="radio-group">
                  {CSM_FORM_STATE_OPTIONS.map((opt) => (
                    <label key={opt.value} className="radio-group__item">
                      <input
                        type="radio"
                        name="csmState"
                        checked={Number(form.state) === opt.value}
                        onChange={() => setForm((prev) => ({ ...prev, state: opt.value }))}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </fieldset>

              <fieldset className="form-fieldset">
                <legend>前台是否允許複製顯示內容</legend>
                <div className="radio-group">
                  {CSM_FORM_COPY_OPTIONS.map((opt) => (
                    <label key={opt.value} className="radio-group__item">
                      <input
                        type="radio"
                        name="csmCopy"
                        checked={Number(form.copyStatus) === opt.value}
                        onChange={() => setForm((prev) => ({ ...prev, copyStatus: opt.value }))}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="form-field">
                <span className="form-field__label">客服圖標</span>
                <div className="carousel-image-upload">
                  {form.image ? (
                    <img src={resolveAdminMediaUrl(form.image)} alt="" className="carousel-thumb-preview" />
                  ) : (
                    <div className="carousel-thumb-placeholder">未設定</div>
                  )}
                  <label className="btn btn-success">
                    {uploadingImage ? '上傳中…' : '上傳客服 logo 圖（120×120）'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      disabled={uploadingImage || submitting}
                      onChange={handleImageUpload}
                    />
                  </label>
                </div>
              </div>

              {form.details.map((detail, index) => (
                <section key={detail.lang} className="static-content-edit-form__section">
                  <h4>{detail._label || detail.lang}</h4>
                  <div className="form-field">
                    <label htmlFor={`csmName-${detail.lang}`}>客服名稱</label>
                    <input
                      id={`csmName-${detail.lang}`}
                      type="text"
                      value={detail.name || ''}
                      onChange={(e) => updateDetail(index, 'name', e.target.value)}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor={`csmBtn-${detail.lang}`}>進入按鈕名稱</label>
                    <input
                      id={`csmBtn-${detail.lang}`}
                      type="text"
                      value={detail.buttonName || ''}
                      onChange={(e) => updateDetail(index, 'buttonName', e.target.value)}
                    />
                  </div>
                </section>
              ))}
            </>
          ) : null}

          <div className="modal-panel__footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
              關閉
            </button>
            <button type="submit" className="btn btn-success" disabled={submitting || loading || uploadingImage}>
              {submitting ? '提交中…' : '提交'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
